import React from 'react';
import {
  Box, Card, CardContent, CardActions, Typography, IconButton,
  Chip, Switch, Tooltip, Grid, Skeleton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

const TRIGGER_CONFIG = {
  inbound: { label: '進線', icon: <PhoneCallbackIcon fontSize="small" />, color: '#1565c0' },
  outbound: { label: '外撥', icon: <PhoneForwardedIcon fontSize="small" />, color: '#2e7d32' },
  interrupt: { label: '中斷', icon: <SyncAltIcon fontSize="small" />, color: '#e65100' },
};

const FlowCardGrid = ({
  flows,
  loading,
  onEdit,
  onToggle,
  onDuplicate,
  onDelete,
}) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map(i => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!flows || flows.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="body1" color="text.secondary">
          尚無語音流程
        </Typography>
        <Typography variant="caption" color="text.secondary">
          從範本建立新的語音流程
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {flows.map(flow => {
        const trigger = TRIGGER_CONFIG[flow.triggerType] || TRIGGER_CONFIG.inbound;
        const dialedNumbers = flow.triggerConditions?.dialed_numbers || [];

        return (
          <Grid item xs={12} sm={6} md={4} key={flow.id}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 },
                opacity: flow.isActive ? 1 : 0.6,
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                {/* 標題列 */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    {flow.name}
                  </Typography>
                  <Tooltip title={flow.isActive ? '啟用中' : '已停用'}>
                    <Switch
                      checked={flow.isActive}
                      onChange={() => onToggle(flow.id)}
                      size="small"
                    />
                  </Tooltip>
                </Box>

                {/* 觸發類型 + 號碼 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Chip
                    icon={trigger.icon}
                    label={trigger.label}
                    size="small"
                    sx={{
                      height: 24,
                      bgcolor: `${trigger.color}10`,
                      color: trigger.color,
                      border: `1px solid ${trigger.color}30`,
                      '& .MuiChip-icon': { color: trigger.color },
                    }}
                  />
                  {flow.scenarioType && (
                    <Chip
                      label={flow.scenarioType}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>

                {/* 綁定號碼 */}
                {dialedNumbers.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    綁定號碼: {dialedNumbers.join(', ')}
                  </Typography>
                )}

                {/* Flow ID */}
                <Typography variant="caption" sx={{ color: '#999' }}>
                  {flow.id}
                </Typography>
              </CardContent>

              <CardActions sx={{ px: 2, pt: 0, pb: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="編輯腳本">
                  <IconButton size="small" onClick={() => onEdit(flow)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="複製">
                  <IconButton size="small" onClick={() => onDuplicate(flow)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="刪除">
                  <IconButton size="small" onClick={() => onDelete(flow)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default FlowCardGrid;
